import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Customer, CustomerWrite, InputMaxLength } from "types"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import { logger } from "utils/secureLogger"
import useCustomers from "@/hooks/useCustomers"
import Form from "components/forms/form"
import FormErrors from "components/formParts/formErrors"
import TextInput from "components/inputs/textInput"
import ProcessingButton from "components/inputs/processingButton"
import EmailInput from "@/components/inputs/emailInput"
import TextareaInput from "@/components/inputs/textareaInput"
import Button from "@/components/inputs/button"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import useLayout from "@/hooks/useLayout"
import Section from "@/components/layout/section"

interface EditCustomerFormProps {
    customer: Customer
    storageId: string
    customerId: string
}

const EditCustomerForm = ({ customer, storageId, customerId }: EditCustomerFormProps) => {

    const { t } = useTranslation()

    const {
        addToast
    } = useLayout()

    const {
        updateCustomerAsync,
        isUpdating
    } = useCustomers({ storageId, customerId })

    // State.
    const [name, setName] = useState(customer?.name || ``)
    const [email, setEmail] = useState(customer?.contactInformation?.email || ``)
    const [phone, setPhone] = useState(customer?.contactInformation?.phone || ``)
    const [notes, setNotes] = useState(customer?.notes || ``)

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    // Handle submit.
    const handleSubmit = async (event: React.FormEvent) => {
        logger.info('Edit customer form submitted successfully')
        event.preventDefault()
        setErrors([])
        
        try {

            const data: CustomerWrite = {
                ids: {
                    storageId,
                    customerId
                },
                _version: 1,
                customerId: parseInt(customerId),
                name,
                notes,
                contactInformation: {
                    email,
                    phone,
                }
            }

            logger.debug('Submitting customer data', data)
            await updateCustomerAsync(data)
            logger.info('Customer updated successfully', { storageId, customerId })
            addToast({ text: t('toastCustomerUpdated'), colorScheme: 'green' })
    } catch (e: unknown) {
            const errorMessage = decodeFirebaseError(e instanceof Error ? e.message : String(e))
            setErrors([errorMessage])
            addToast({ text: errorMessage, colorScheme: 'red' })
            logger.error('Failed to update customer', { error: errorMessage, storageId, customerId })
        }
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle email change.
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    }

    // Handle phone change.
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(e.target.value)
    }

    // Handle notes change.
    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(e.target.value)
    }

    // Return the JSX.
    return (
        <Form onSubmit={handleSubmit}>

            <Section>

                <TextInput
                    label={t('labelCustomerId')}
                    value={customer?.customerId?.toString() || ''}
                    onChange={() => {}}
                    required
                    disabled
                    maxLength={InputMaxLength.short}
                />

                <TextInput
                    label={t('labelCustomerName')}
                    value={name}
                    onChange={handleNameChange}
                    required
                    maxLength={InputMaxLength.short}
                />

                <EmailInput
                    label={t('labelCustomerEmail')}
                    value={email}
                    onChange={handleEmailChange}
                    maxLength={InputMaxLength.medium}
                />

                <TextInput
                    label={t('labelCustomerPhone')}
                    value={phone}
                    onChange={handlePhoneChange}
                    maxLength={InputMaxLength.short}
                />

                <TextareaInput
                    label={t('labelCustomerNotes')}
                    value={notes}
                    onChange={handleNotesChange}
                    maxLength={InputMaxLength.extraLong}
                />

            </Section>

            <Section>
                <div className={`input-holder`}>
                    {isUpdating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary"><AddIcon /><span>{t('buttonSaveChanges')}</span></Button>
                    )}
                </div>
            </Section>
            
            <FormErrors errors={errors} />

        </Form>
    )
}

export default EditCustomerForm
